import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import Header from "../../components/Header/Header";
import DataTable from "../../components/table/Table";
import rootStores from "../../stores";
import {
  getAllAreaCodes,
  getAllPriceBandsCodes,
  getAllClubsCodes,
  getAllEventsByClub,
  getAllStadiumsCodes,
  getTicketsCompareData,
} from "../../fetchers/RFIDTickets";
import { getColumns } from "./consts";
import { useTranslation } from "react-i18next";
import { toJS } from "mobx";

const RFIDTicketsStore = rootStores.RFIDTicketsStore;

/* ---------- shared helpers ---------- */

const fetchEventsByClub = (clubPrefix: string) => {
  if (!clubPrefix || clubPrefix === "ADMIN") {
    RFIDTicketsStore.setEvents([]);
    return;
  }

  getAllEventsByClub(clubPrefix)
    .then(events => RFIDTicketsStore.setEvents(events))
    .catch(console.error);
};

const fetchFiltersData = () => {
  getAllAreaCodes().then(areas => {
    RFIDTicketsStore.setAreas(areas);
    RFIDTicketsStore.setAllAreasCodes(areas);
  });

  getAllStadiumsCodes().then(stadiums => {
    RFIDTicketsStore.setStadiums(stadiums);
    RFIDTicketsStore.setAllStadiumsCodes(stadiums);
  });

  getAllPriceBandsCodes().then(priceBands => {
    RFIDTicketsStore.setPriceBands(priceBands);
  });

  getAllClubsCodes().then(clubs => {
    RFIDTicketsStore.setClubs(clubs);
    RFIDTicketsStore.setAllClubsCodes(clubs);

    const selectedClub = RFIDTicketsStore.club ?? clubs[0];

    RFIDTicketsStore.setClub(selectedClub);
    RFIDTicketsStore.setFilterField("clubPrefix", selectedClub?.clubPrefix);

    fetchEventsByClub(selectedClub?.clubPrefix);
  });
};

/* ---------- component ---------- */

const TicketsCompareComponent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language as "he" | "en";
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(100);
  const [isLoading, setIsLoading] = useState(false);
  const [evaXMLCount, setEvaXMLCount] = useState<number | null>(null);
  const [fortressCount, setFortressCount] = useState<number | null>(null);

  const filter = toJS(RFIDTicketsStore.filter);
  const paginatedRows = useMemo(() => {
    const start = page * pageSize;
    const end = start + pageSize;
    return rows.slice(start, end);
  }, [rows, page, pageSize]);

  /* ---------- lifecycle ---------- */

  useEffect(() => {
    fetchFiltersData();
    return () => RFIDTicketsStore.clearFilter();
  }, []);

  useEffect(() => {
    fetchEventsByClub(RFIDTicketsStore.club?.clubPrefix);
  }, [RFIDTicketsStore.club?.clubPrefix]);

  /* ---------- handlers ---------- */

  const handleFilter = async () => {
    if (!filter.event) {
      alert(t("ticketsCompare.selectEventError"));
      return;
    }

    setIsLoading(true);

    try {
      const res = await getTicketsCompareData(
        RFIDTicketsStore.club,
        filter.event
      );
  
      const differentObjects = Array.isArray(res?.differentObjects)
        ? res.differentObjects
        : [];
  
      const mappedRows = differentObjects.map(
        (item: any, index: number) => ({ ...item, id: index })
      );

      setRows(mappedRows);
      setEvaXMLCount(res?.evaXMLTicketsLength ?? 0);
      setFortressCount(res?.fortressTicketsLength ?? 0);

      setPage(0);
    } catch (error) {
      console.error("Failed to get ticket comparison", error);
  
      setRows([]);
      setEvaXMLCount(0);
      setFortressCount(0);
      setPage(0);
    } finally {
      setIsLoading(false);
    }
  };


  const handleSync = async () => {
    if (!filter.event) {
      alert(t("ticketsCompare.selectEventError"));
      return;
    }

    try {
      await RFIDTicketsStore.createAllEventimTicketsAtFortress(filter.event, RFIDTicketsStore.club);
      await handleFilter();
    } catch (err) {
      console.error("Sync failed", err);
    }
  };

  /* ---------- render ---------- */

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "row-reverse" }}>
      <div
        style={{
          margin: "30px 50px 0 50px",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        <Header
          handleFilter={handleFilter}
          isRightDialogOpen={false}
          filterFields={["event"]}
          handleSync={handleSync}
          isSyncing={true}
        />

        <div style={{ display: "flex", justifyContent: "flex-end", margin: "2vh" }}>
          {evaXMLCount !== null && (
            <div style={{ margin: "0 4vh" }}>
              {t("ticketsCompare.evaXmlCount")} : {evaXMLCount}
            </div>
          )}
          {fortressCount !== null && (
            <div style={{ margin: "0 4vh" }}>
              {t("ticketsCompare.fortressCount")} : {fortressCount}
            </div>
          )}
        </div>

        <DataTable
          rows={paginatedRows}
          columns={getColumns(language)}
          isLoading={isLoading}
          withCheckbox={false}
          withAllCheckbox={false}
          paginationMode="server"
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalFiltered={rows.length}
          totalRecordsInDB={rows.length}
        />
      </div>
    </div>
  );
};

export default observer(TicketsCompareComponent);
